// Msg #800 — Shared music-embed sanitizer + autoplay rewriter used by both
// the character-profile inline player (ProfileModals.jsx / MusicEmbed) and
// the floating Adventure Soundtrack player (AdventureSoundtrackPlayer.jsx).
// Kept in a single module so the transform runs exactly once per music_html
// and both consumers stay in lock-step.
//
// Transformations:
//   • Strips <script> and inline on* handlers (defensive XSS guard).
//   • Rewrites <iframe src> for YouTube, Spotify, SoundCloud, Vimeo so the
//     track starts automatically.
//   • Injects/merges `allow="autoplay; encrypted-media; picture-in-picture"`
//     on every iframe.  Guarantees ONE whitespace between the injected
//     `allow` attribute and the trailing `src=` (Msg #800 fix).
//   • Adds `autoplay` to bare <audio> / <video> tags.
export const prepareMusicHtml = (raw) => {
  if (!raw) return '';
  let html = String(raw);
  html = html.replace(/<script[\s\S]*?<\/script>/gi, '');
  html = html.replace(/\son\w+="[^"]*"/gi, '');
  html = html.replace(/\son\w+='[^']*'/gi, '');
  html = html.replace(/<iframe([^>]*?)src=(["'])([^"']+)\2/gi, (m, attrs, q, src) => {
    let newSrc = src;
    try {
      const u = new URL(src, 'https://example.com');
      if (/(?:youtube\.com|youtu\.be)/i.test(u.hostname)) {
        u.searchParams.set('autoplay', '1');
        u.searchParams.set('mute', '0');
        newSrc = u.toString();
      } else if (/spotify\.com/i.test(u.hostname)) {
        if (!u.searchParams.has('autoplay')) u.searchParams.set('autoplay', '1');
        newSrc = u.toString();
      } else if (/soundcloud\.com/i.test(u.hostname)) {
        u.searchParams.set('auto_play', 'true');
        newSrc = u.toString();
      } else if (/vimeo\.com/i.test(u.hostname)) {
        u.searchParams.set('autoplay', '1');
        newSrc = u.toString();
      }
    } catch { /* leave src untouched if URL parse fails */ }

    let newAttrs = attrs;
    if (/\ballow=/i.test(newAttrs)) {
      newAttrs = newAttrs.replace(/\ballow=(["'])([^"']*)\1/i, (mm, qq, val) =>
        `allow=${qq}${val.includes('autoplay') ? val : `autoplay; ${val}`}${qq}`);
    } else {
      newAttrs = `${newAttrs} allow="autoplay; encrypted-media; picture-in-picture"`;
    }
    // Guarantee exactly one space between the (possibly re-written) attribute
    // list and the trailing `src=` — otherwise browsers parse the run as a
    // malformed attribute and silently disable autoplay.
    newAttrs = `${newAttrs.replace(/\s*$/, '')} `;
    return `<iframe${newAttrs}src=${q}${newSrc}${q}`;
  });
  html = html.replace(/<audio(\s[^>]*)?>/gi, (m, attrs = '') =>
    /\bautoplay\b/i.test(attrs) ? m : `<audio${attrs || ''} autoplay>`);
  html = html.replace(/<video(\s[^>]*)?>/gi, (m, attrs = '') =>
    /\bautoplay\b/i.test(attrs) ? m : `<video${attrs || ''} autoplay>`);
  return html;
};

export default prepareMusicHtml;
